export const agentsPillar = {
  slug: "conversational-ai-agents",
  title: "Conversational AI Voice Agents Built on ElevenLabs | Tier-1.io",
  metaDescription:
    "Tier-1.io designs, builds, and supports custom conversational AI voice agents on ElevenLabs Agents — phone reception, lead qualification, support, scheduling, and outbound calling that connect to the tools you already run.",
  heroKicker: "Conversational AI, built to ship",
  heroTitle: "Custom conversational AI voice agents, built on ElevenLabs",
  heroDescription:
    "We design, build, and support voice agents on ElevenLabs Agents that answer calls, qualify leads, book appointments, and resolve support questions — wired into your phone system, calendar, CRM, and help desk so the conversation actually does the work.",
  intro:
    "A demo voice agent is easy. A voice agent your business can depend on is not. Tier-1.io builds production conversational AI on ElevenLabs Agents: scoped to a real workflow, connected to your systems, guardrailed for the cases that matter, and supported after launch. We start with one call flow worth fixing and grow from there.",
  capabilities: [
    {
      title: "Inbound call handling",
      description:
        "Answer every call, route by intent, take messages, and book appointments without sending callers to voicemail.",
    },
    {
      title: "Outbound calling campaigns",
      description:
        "Run confirmations, follow-ups, renewals, and outreach with natural conversation, voicemail handling, and logged outcomes.",
    },
    {
      title: "Lead qualification",
      description:
        "Capture and qualify inbound leads, score budget and timeline, and route the right ones to the right rep.",
    },
    {
      title: "Support deflection",
      description:
        "Resolve common questions, check order and account status, and escalate cleanly to a human with full context.",
    },
    {
      title: "System integration",
      description:
        "Connect the agent to your calendar, CRM, help desk, telephony, and knowledge base so conversations update real records.",
    },
    {
      title: "Guardrails and handoff",
      description:
        "Define what the agent can and cannot do, where humans take over, and how every interaction is logged for review.",
    },
  ],
  buildOn: {
    title: "Built on ElevenLabs Agents",
    description:
      "ElevenLabs gives the agent natural, low-latency speech and turn-taking. We build everything around it that turns a voice into a reliable business system.",
    points: [
      "Conversation design, prompts, and tools scoped to one workflow at a time",
      "Telephony, calendar, CRM, and knowledge-base integrations that read and write real data",
      "Guardrails, escalation paths, and human handoff for the cases that matter",
      "Testing against realistic calls, then monitoring and tuning after launch",
    ],
  },
  faqs: [
    {
      question: "What is a conversational AI voice agent?",
      answer:
        "It is software that holds a natural spoken conversation over the phone or web — answering questions, collecting information, booking appointments, or making outbound calls — and takes action in your systems based on what was said. We build these on ElevenLabs Agents.",
    },
    {
      question: "Do you only build voice, or text chat too?",
      answer:
        "Both. ElevenLabs Agents power the voice experience, and we can pair it with web chat and messaging when a workflow spans channels. Most clients start with phone calls because that is where the manual work and missed opportunities pile up.",
    },
    {
      question: "Can the agent connect to our existing tools?",
      answer:
        "Yes. The value is in the integrations. We connect agents to your phone system, calendar, CRM, help desk, and internal data so conversations update real records instead of producing transcripts no one reads.",
    },
    {
      question: "How do you keep the agent from saying the wrong thing?",
      answer:
        "We scope each agent to a defined job, ground it in approved source material, add guardrails for sensitive topics, and design clear escalation to a human. We test against realistic calls before launch and monitor afterward.",
    },
    {
      question: "Where do you work?",
      answer:
        "Conversational AI is delivered remotely, so we build for businesses across the United States. Discovery, design, integration, and support all happen without anyone needing to be on-site.",
    },
  ],
  cta: "Book a conversational AI discovery call",
};

export const agentIndustries = [
  {
    slug: "conversational-ai-agents/healthcare",
    industry: "Healthcare & Medical",
    title: "AI Voice Agents for Healthcare & Medical Practices | Tier-1.io",
    metaDescription:
      "Custom conversational AI voice agents for medical, dental, and veterinary practices — front-desk reception, scheduling, and appointment reminders built on ElevenLabs and connected to your scheduling system.",
    heroKicker: "Conversational AI for healthcare",
    heroTitle: "AI voice agents for medical, dental, and veterinary practices",
    heroDescription:
      "Front desks miss calls when they are busy with patients. We build voice agents on ElevenLabs that answer every call, collect intake details, schedule and confirm visits, and route urgent issues to staff.",
    intro:
      "Medical front desks juggle patients in the room and patients on the phone, and the phone usually loses. A conversational AI agent answers consistently, collects the right information, books and confirms appointments, and hands off cleanly when a human is needed — so no call goes unanswered and no reminder gets skipped.",
    painPoints: [
      "Calls go to voicemail while staff handle in-office patients",
      "No-shows pile up because reminders depend on someone having time",
      "After-hours and weekend calls are missed entirely",
      "Intake details arrive incomplete and slow down the visit",
    ],
    agents: [
      {
        name: "Healthcare Receptionist",
        description:
          "Medical facility front-desk reception that collects patient information and schedules visits.",
        integrations: 2,
      },
      {
        name: "Appointment Reminder & Confirmation",
        description:
          "Outbound calls that confirm or reschedule upcoming patient appointments and update the calendar.",
        integrations: 1,
      },
      {
        name: "Pet Care / Veterinary Receptionist",
        description:
          "Veterinary and pet-service reception for appointments, pricing, and emergency routing.",
        integrations: 1,
      },
      {
        name: "Appointment Scheduler",
        description:
          "Checks availability, books appointments, and sends confirmations for any practice type.",
        integrations: 1,
      },
    ],
    outcomes: [
      "Every call answered, even during peak hours and after close",
      "Fewer no-shows from consistent reminders and easy rescheduling",
      "Complete intake captured before the patient arrives",
      "Urgent situations routed to staff immediately",
    ],
    integrationsNote:
      "Connects to scheduling and practice-management systems. For practices handling protected health information, we scope data handling and access carefully and keep a human in the loop where it matters.",
    faqs: [
      {
        question: "Can the agent handle our scheduling system?",
        answer:
          "Yes. We integrate with common scheduling and practice-management tools so the agent reads real availability and writes confirmed appointments, rather than just taking a message.",
      },
      {
        question: "Is this appropriate for protected health information?",
        answer:
          "We design healthcare agents conservatively: scoped tasks, careful data handling, limited access, and human handoff for clinical or sensitive matters. We will review your compliance requirements during scoping before building anything that touches patient data.",
      },
      {
        question: "What happens with an emergency call?",
        answer:
          "The agent recognizes urgent language and routes the caller to staff or an emergency line immediately, following rules you define rather than trying to handle it itself.",
      },
    ],
    cta: "Book a healthcare voice agent discovery call",
  },
  {
    slug: "conversational-ai-agents/hospitality",
    industry: "Hospitality, Restaurants & Travel",
    title: "AI Voice Agents for Restaurants, Hotels & Hospitality | Tier-1.io",
    metaDescription:
      "Conversational AI voice agents for restaurants, hotels, and travel — reservations, phone orders, concierge, and after-hours answering built on ElevenLabs and wired into your booking tools.",
    heroKicker: "Conversational AI for hospitality",
    heroTitle: "AI voice agents for restaurants, hotels, and travel",
    heroDescription:
      "A ringing phone during the dinner rush is a missed reservation or a missed order. We build voice agents on ElevenLabs that take bookings and orders, answer guest questions, and cover the hours staff cannot.",
    intro:
      "Hospitality runs on the phone, and the phone rings hardest when staff are busiest. Conversational AI takes reservations and orders, answers the same questions guests always ask, and keeps working after hours — so the front of house can focus on the guests in front of them.",
    painPoints: [
      "Reservations and orders missed during the rush",
      "Repetitive questions about hours, menu, parking, and policies",
      "After-hours calls that go unanswered",
      "Order details taken down wrong over a noisy line",
    ],
    agents: [
      {
        name: "Restaurant Host",
        description:
          "Front-of-house host that fields inquiries and books reservations.",
        integrations: 2,
      },
      {
        name: "Restaurant Phone Order Taker",
        description:
          "Takes pickup and delivery orders, handles customizations, and confirms totals.",
        integrations: 1,
      },
      {
        name: "Hotel Reservation Agent",
        description:
          "Books reservations, checks room availability, and handles modifications and cancellations.",
        integrations: 0,
      },
      {
        name: "Hospitality Concierge",
        description:
          "Front-desk concierge that handles guest inquiries and issues.",
        integrations: 1,
      },
      {
        name: "Vacation Rental Host Agent",
        description:
          "Guest communication for vacation rentals — check-in, local tips, and issue handling.",
        integrations: 2,
      },
      {
        name: "Catering & Event Inquiry Agent",
        description:
          "Collects event details, answers menu questions, and coordinates proposals.",
        integrations: 1,
      },
      {
        name: "Travel Agency / Trip Planner",
        description:
          "Handles trip inquiries, recommends packages, and collects traveler preferences.",
        integrations: 1,
      },
      {
        name: "After-Hours Answering Service",
        description:
          "Handles calls outside business hours, takes messages, and routes emergencies.",
        integrations: 2,
      },
    ],
    outcomes: [
      "More reservations and orders captured during peak times",
      "Guests get instant answers to routine questions",
      "Phone coverage that extends past staffed hours",
      "Accurate orders and bookings written straight to your system",
    ],
    integrationsNote:
      "Connects to reservation, ordering, and property-management platforms so bookings and orders land where your team already works.",
    faqs: [
      {
        question: "Can it take orders with modifications?",
        answer:
          "Yes. The agent walks through the menu, handles customizations and substitutions, confirms the total, and sends the order to your point-of-sale or ordering system.",
      },
      {
        question: "Will it sound robotic to our guests?",
        answer:
          "ElevenLabs voices are natural and responsive, with real turn-taking. We tune tone and pacing to match your brand so it feels like a capable host, not an IVR menu.",
      },
      {
        question: "Can it cover only nights and weekends?",
        answer:
          "Absolutely. Many venues run the agent only after hours or during peak periods, with calls going to staff the rest of the time.",
      },
    ],
    cta: "Book a hospitality voice agent discovery call",
  },
  {
    slug: "conversational-ai-agents/real-estate",
    industry: "Real Estate & Property",
    title: "AI Voice Agents for Real Estate & Property Management | Tier-1.io",
    metaDescription:
      "Conversational AI voice agents for real estate and property management — lead follow-up, showing scheduling, tenant inquiries, and mortgage pre-qualification built on ElevenLabs and connected to your CRM.",
    heroKicker: "Conversational AI for real estate",
    heroTitle: "AI voice agents for real estate and property management",
    heroDescription:
      "In real estate, the first agent to call back usually wins. We build voice agents on ElevenLabs that follow up with leads in seconds, qualify interest, book showings, and field tenant inquiries.",
    intro:
      "Leads go cold fast and tenants expect quick answers. Conversational AI calls new leads the moment they come in, qualifies budget and timeline, books showings against a real calendar, and handles routine tenant requests — so opportunities do not slip while you are with a client.",
    painPoints: [
      "Leads go cold before an agent can call back",
      "Showings are hard to schedule around a full day",
      "Tenant calls interrupt everything else",
      "Unqualified inquiries eat time that should go to real buyers",
    ],
    agents: [
      {
        name: "Real Estate Lead Follow-Up",
        description:
          "Follows up with leads, qualifies buyer interest and budget, and books property showings.",
        integrations: 2,
      },
      {
        name: "Property Management",
        description:
          "Handles tenant inquiries, maintenance requests, and routing for property managers.",
        integrations: 3,
      },
      {
        name: "Mortgage Pre-Qualification Agent",
        description:
          "Collects financial information to pre-qualify mortgage and loan leads.",
        integrations: 2,
      },
    ],
    outcomes: [
      "Instant lead follow-up that beats slower competitors",
      "Showings booked directly onto your calendar",
      "Routine tenant requests handled without interrupting your day",
      "Only qualified buyers routed to you",
    ],
    integrationsNote:
      "Connects to your CRM, calendar, and lead sources so every conversation updates the record and the right follow-up happens automatically.",
    faqs: [
      {
        question: "How fast can it follow up with a new lead?",
        answer:
          "Within seconds of a form submission or inbound call. Speed-to-lead is the single biggest driver of conversion, and an agent never gets stuck in a showing or a meeting.",
      },
      {
        question: "Can it book showings on my calendar?",
        answer:
          "Yes. It checks your real availability and books confirmed showings, then logs the lead and outcome in your CRM.",
      },
      {
        question: "Will it pester leads or feel pushy?",
        answer:
          "You set the cadence and tone. We design follow-up that is prompt and helpful, with clear opt-out handling, so it builds trust instead of burning leads.",
      },
    ],
    cta: "Book a real estate voice agent discovery call",
  },
  {
    slug: "conversational-ai-agents/home-services",
    industry: "Home & Local Services",
    title: "AI Voice Agents for Home Services & Local Trades | Tier-1.io",
    metaDescription:
      "Conversational AI voice agents for HVAC, plumbing, electrical, cleaning, auto, salon, and fitness businesses — lead qualification, scheduling, and quotes built on ElevenLabs and connected to your booking tools.",
    heroKicker: "Conversational AI for local services",
    heroTitle: "AI voice agents for home services and local trades",
    heroDescription:
      "A missed call is a booked job that went to your competitor. We build voice agents on ElevenLabs that answer, qualify the work, quote when they can, and put jobs on the schedule.",
    intro:
      "When a tech is on a ladder or a stylist is with a client, the phone keeps ringing — and every missed call is revenue walking out the door. Conversational AI answers, qualifies the job, gives ballpark pricing where appropriate, and books the appointment, so you capture the work without hiring a full-time receptionist.",
    painPoints: [
      "Calls missed while crews are on the job",
      "Quote and pricing questions repeated all day",
      "Scheduling gaps and double-bookings",
      "Tire-kicker calls mixed in with real jobs",
    ],
    agents: [
      {
        name: "Home Services Qualifier",
        description:
          "Qualifies leads for HVAC, plumbing, electrical, and cleaning, and schedules technicians.",
        integrations: 2,
      },
      {
        name: "Solar / Home Improvement Lead Qualifier",
        description:
          "Qualifies leads for solar, roofing, and window installations.",
        integrations: 1,
      },
      {
        name: "Cleaning / Janitorial Service Agent",
        description:
          "Handles quote requests, scheduling, and recurring service plans.",
        integrations: 1,
      },
      {
        name: "Moving / Relocation Quote Agent",
        description:
          "Collects move details and provides estimates for moving companies.",
        integrations: 2,
      },
      {
        name: "Automotive Service / Repair Receptionist",
        description:
          "Handles service pricing, appointments, and diagnostic questions for auto shops.",
        integrations: 1,
      },
      {
        name: "Salon / Spa / Beauty Receptionist",
        description:
          "Handles service pricing, appointment booking, and stylist recommendations.",
        integrations: 1,
      },
      {
        name: "Fitness / Wellness Booking Agent",
        description:
          "Handles class schedules, bookings, and membership questions for gyms and studios.",
        integrations: 1,
      },
    ],
    outcomes: [
      "Captured jobs that would have gone to voicemail",
      "Consistent answers on pricing and availability",
      "A fuller, cleaner schedule with fewer conflicts",
      "Qualified jobs prioritized over time-wasters",
    ],
    integrationsNote:
      "Connects to your scheduling and dispatch tools so booked jobs land on the calendar and the right tech gets assigned.",
    faqs: [
      {
        question: "Can it give customers a price?",
        answer:
          "For standard services, yes — we configure ballpark pricing and rules you approve. For anything that needs an on-site look, the agent books a quote visit instead of guessing.",
      },
      {
        question: "Can it dispatch to the right technician?",
        answer:
          "It can book against your schedule and apply routing rules. Full dispatch automation depends on your field-service software, which we will review during scoping.",
      },
      {
        question: "Does this replace my office staff?",
        answer:
          "It removes the repetitive phone load so your team handles the work that needs judgment. Most owners use it to stop missing calls, not to cut staff.",
      },
    ],
    cta: "Book a home services voice agent discovery call",
  },
  {
    slug: "conversational-ai-agents/sales",
    industry: "Sales & Lead Generation",
    title: "AI Voice Agents for Sales & Lead Generation | Tier-1.io",
    metaDescription:
      "Conversational AI voice agents for sales teams — inbound lead qualification, outbound dialing, appointment setting, and demo booking built on ElevenLabs and connected to your CRM.",
    heroKicker: "Conversational AI for sales",
    heroTitle: "AI voice agents for sales and lead generation",
    heroDescription:
      "Reps should spend time with qualified buyers, not chasing dials and dead leads. We build voice agents on ElevenLabs that qualify inbound demand, run outbound campaigns, and book meetings straight onto reps' calendars.",
    intro:
      "Pipeline stalls in the gap between a lead raising its hand and a rep getting to it. Conversational AI closes that gap: it qualifies inbound leads instantly, dials outbound lists with natural conversation and voicemail handling, and books meetings on the right rep's calendar — logging every outcome in your CRM.",
    painPoints: [
      "Slow follow-up lets warm leads cool off",
      "Reps burn hours on dials, voicemails, and unqualified leads",
      "Inconsistent qualification means bad meetings on the calendar",
      "Outcomes never make it cleanly into the CRM",
    ],
    agents: [
      {
        name: "Inbound Lead Qualifier",
        description:
          "Qualifies inbound leads from web forms and ads, assesses budget and timeline, and routes to the right rep.",
        integrations: 2,
      },
      {
        name: "Outbound Sales Representative",
        description:
          "Conducts outbound calls and books meetings.",
        integrations: 1,
      },
      {
        name: "Appointment Setter",
        description:
          "Books demos and meetings with warm leads, checks calendar availability, and confirms appointments.",
        integrations: 2,
      },
      {
        name: "B2B Demo Qualifier",
        description:
          "Qualifies enterprise leads by company size, use case, and timeline.",
        integrations: 2,
      },
      {
        name: "Marketing Lead Capture Agent",
        description:
          "Captures website leads, collects contact information, and qualifies interest.",
        integrations: 1,
      },
      {
        name: "Win-Back / Re-engagement Caller",
        description:
          "Outbound calls to lapsed customers that understand why they left.",
        integrations: 2,
      },
      {
        name: "Outbound Dialer with Voicemail Handling",
        description:
          "Handles live calls and voicemail drops, and logs outcomes for follow-up.",
        integrations: 2,
      },
      {
        name: "IVR-Navigating Outbound Caller",
        description:
          "Navigates phone trees to reach decision makers and deliver a pitch.",
        integrations: 2,
      },
    ],
    outcomes: [
      "Instant qualification of inbound leads",
      "Reps' calendars filled with qualified meetings",
      "Outbound volume without adding headcount",
      "Every call outcome logged in the CRM",
    ],
    integrationsNote:
      "Connects to your CRM, calendar, and dialer so qualification, routing, and meeting booking happen inside your existing sales stack.",
    faqs: [
      {
        question: "Will prospects know they are talking to AI?",
        answer:
          "We design for transparency where it is expected or required, and tune the conversation to be useful and respectful. The goal is qualified, willing meetings, not tricking anyone.",
      },
      {
        question: "Can it book onto a specific rep's calendar?",
        answer:
          "Yes. It applies your routing rules — by territory, product, or round-robin — and books confirmed meetings on the right rep's calendar.",
      },
      {
        question: "Is outbound calling compliant?",
        answer:
          "We build outbound agents with calling rules, opt-out handling, and time-of-day constraints. You are responsible for your call lists and consent; we will design the guardrails around your compliance requirements.",
      },
    ],
    cta: "Book a sales voice agent discovery call",
  },
  {
    slug: "conversational-ai-agents/customer-support",
    industry: "Customer Support & Success",
    title: "AI Voice Agents for Customer Support & Success | Tier-1.io",
    metaDescription:
      "Conversational AI voice agents for customer support and ecommerce — order status, technical troubleshooting, returns, and subscription management built on ElevenLabs and connected to your help desk.",
    heroKicker: "Conversational AI for support",
    heroTitle: "AI voice agents for customer support and success",
    heroDescription:
      "Most support calls are the same handful of questions. We build voice agents on ElevenLabs that resolve the routine ones, check real order and account data, and escalate the rest to your team with full context.",
    intro:
      "Support teams drown in repeat questions while genuinely hard tickets wait. Conversational AI handles the routine volume — order status, returns, basic troubleshooting, subscription changes — by looking up real data and acting on it, then escalates cleanly so agents start with context instead of from scratch.",
    painPoints: [
      "Repeat questions consume most of the queue",
      "Customers wait on hold for simple answers",
      "Agents escalate with no context attached",
      "After-hours support is limited or unavailable",
    ],
    agents: [
      {
        name: "Customer Support Representative",
        description:
          "Fields support inquiries and resolves common issues.",
        integrations: 2,
      },
      {
        name: "Order Status & Tracking",
        description:
          "Handles order inquiries, tracking, delivery estimates, and basic returns.",
        integrations: 2,
      },
      {
        name: "Technical Support Agent",
        description:
          "Troubleshoots product issues step by step and escalates with a support ticket.",
        integrations: 2,
      },
      {
        name: "Escalated Support Specialist",
        description:
          "Picks up escalated tickets, reproduces issues, applies known workarounds, and escalates to engineering when needed.",
        integrations: 2,
      },
      {
        name: "Ecommerce Customer Experience",
        description:
          "Shopping assistant that also handles support inquiries for ecommerce sites.",
        integrations: 2,
      },
      {
        name: "Membership & Subscription Support",
        description:
          "Handles membership status, upgrades, downgrades, and cancellation saves.",
        integrations: 3,
      },
      {
        name: "Renewal & Expansion Agent",
        description:
          "Outbound success agent that drives renewals and surfaces expansion opportunities.",
        integrations: 2,
      },
    ],
    outcomes: [
      "Routine tickets resolved without an agent",
      "Shorter hold times and 24/7 coverage",
      "Escalations that arrive with full context",
      "More renewals and saved cancellations",
    ],
    integrationsNote:
      "Connects to your help desk, order system, and account data so the agent answers from real records and opens tickets with the details already filled in.",
    faqs: [
      {
        question: "Can it look up a real order or account?",
        answer:
          "Yes. We integrate with your order and account systems so the agent gives accurate, customer-specific answers and can take action — not generic responses.",
      },
      {
        question: "How does escalation to a human work?",
        answer:
          "When the agent hits something outside its scope, it hands off to your team and passes along the full conversation and any data it gathered, so the customer does not have to repeat themselves.",
      },
      {
        question: "Can it handle cancellations and saves?",
        answer:
          "It can process routine subscription changes and run approved save offers before a cancellation, escalating to a person when the situation calls for it.",
      },
    ],
    cta: "Book a support voice agent discovery call",
  },
  {
    slug: "conversational-ai-agents/finance",
    industry: "Finance, Insurance & Collections",
    title: "AI Voice Agents for Finance, Insurance & Collections | Tier-1.io",
    metaDescription:
      "Conversational AI voice agents for insurance, accounting, lending, and collections — quote intake, receivables, and account servicing built on ElevenLabs with compliance-aware guardrails.",
    heroKicker: "Conversational AI for finance",
    heroTitle: "AI voice agents for finance, insurance, and collections",
    heroDescription:
      "Financial conversations are repetitive but sensitive. We build voice agents on ElevenLabs that collect quote details, make respectful collections calls, and service accounts — with the guardrails these workflows require.",
    intro:
      "Quote intake, payment reminders, and account questions are high-volume and rule-bound — a good fit for conversational AI, provided it is built carefully. We design finance agents that follow your scripts and compliance rules, collect or deliver exactly the right information, and escalate anything that needs a licensed human.",
    painPoints: [
      "Quote and intake calls take time but follow the same script",
      "Receivables outreach is uncomfortable and inconsistent",
      "Routine account questions tie up specialized staff",
      "Compliance requirements make ad-hoc calling risky",
    ],
    agents: [
      {
        name: "Insurance Advisor",
        description:
          "Reception agent for an insurance or financial firm that handles inquiries and routing.",
        integrations: 2,
      },
      {
        name: "Insurance Quote Intake",
        description:
          "Collects details for auto, home, life, or health insurance quotes.",
        integrations: 2,
      },
      {
        name: "Accounts Receivable Specialist",
        description:
          "Makes respectful outbound calls to customers with overdue invoices.",
        integrations: 1,
      },
      {
        name: "Collections / Payment Recovery Agent",
        description:
          "Contacts past-due accounts and arranges payments, built to follow FDCPA rules.",
        integrations: 2,
      },
      {
        name: "Accounting / Tax Firm Receptionist",
        description:
          "Handles appointments, tax-deadline questions, and document follow-ups for CPA firms.",
        integrations: 1,
      },
      {
        name: "Utility / Telecom Customer Intake",
        description:
          "Handles account inquiries, new-service signups, moves, and billing questions.",
        integrations: 2,
      },
    ],
    outcomes: [
      "Faster, consistent quote and intake capture",
      "Respectful, compliant receivables and collections outreach",
      "Routine account questions handled without specialists",
      "Clear escalation for anything requiring a licensed human",
    ],
    integrationsNote:
      "Connects to your CRM, billing, and policy systems. We design data handling, scripting, and disclosures around your compliance requirements and keep licensed work with your team.",
    faqs: [
      {
        question: "Can collections calls be FDCPA-compliant?",
        answer:
          "We build collections agents to follow the disclosures, timing, and conduct rules you specify, with opt-out handling and logging. You remain responsible for compliance; we design the guardrails to support it and review them with you before launch.",
      },
      {
        question: "Will it give financial or insurance advice?",
        answer:
          "No. These agents handle intake, servicing, and routing. Anything requiring a licensed professional is escalated to your staff by design.",
      },
      {
        question: "How is sensitive data handled?",
        answer:
          "We scope exactly what the agent can access and store, limit it to the task, and design the integration around your data-handling and compliance requirements.",
      },
    ],
    cta: "Book a finance voice agent discovery call",
  },
  {
    slug: "conversational-ai-agents/it-hr-operations",
    industry: "IT, HR & Internal Operations",
    title: "AI Voice Agents for IT, HR & Internal Operations | Tier-1.io",
    metaDescription:
      "Internal conversational AI voice agents for IT help desk, HR hotlines, onboarding, and incident response — built on ElevenLabs and connected to your internal systems and knowledge base.",
    heroKicker: "Conversational AI for internal teams",
    heroTitle: "AI voice agents for IT, HR, and internal operations",
    heroDescription:
      "Your internal teams field the same employee questions all day. We build voice agents on ElevenLabs that handle password resets, benefits questions, onboarding, and incident triage — so specialists focus on real problems.",
    intro:
      "IT and HR spend a surprising share of their time on repeatable internal requests. Conversational AI gives employees a fast, consistent first line for password resets, access requests, benefits questions, and onboarding steps, while routing genuine incidents to the right on-call person with the details already gathered.",
    painPoints: [
      "Help desk buried in password resets and access requests",
      "HR answering the same benefits and PTO questions repeatedly",
      "New hires waiting on someone to walk them through Day 1",
      "Incidents reach on-call without triage or context",
    ],
    agents: [
      {
        name: "IT Help Desk / Internal Support",
        description:
          "Internal IT support for password resets, VPN, and software troubleshooting.",
        integrations: 1,
      },
      {
        name: "IT Access Request Agent",
        description:
          "Handles employee access requests for VPN, software, repos, and restricted systems.",
        integrations: 1,
      },
      {
        name: "Incident Response Coordinator",
        description:
          "Triages incoming incidents, classifies severity, and pages the right on-call.",
        integrations: 1,
      },
      {
        name: "New Hire Onboarding Assistant",
        description:
          "Guides new hires through Day 1 — role-specific path, benefits enrollment, and team intros.",
        integrations: 2,
      },
      {
        name: "Employee Benefits / HR Hotline",
        description:
          "Answers employee questions about health, PTO, 401(k), and payroll.",
        integrations: 1,
      },
      {
        name: "Internal Knowledge Assistant",
        description:
          "Internal knowledge concierge that answers process and policy questions.",
        integrations: 1,
      },
    ],
    outcomes: [
      "Common IT and HR requests resolved instantly",
      "Specialists freed for work that needs judgment",
      "New hires onboarded consistently from Day 1",
      "Incidents triaged and routed with context",
    ],
    integrationsNote:
      "Connects to your identity, ticketing, HR, and knowledge systems so the agent can act on requests and answer from approved internal sources.",
    faqs: [
      {
        question: "Can it actually reset a password or grant access?",
        answer:
          "Within the limits you set. We integrate with your identity and ticketing systems so the agent can perform approved actions and route the rest to a human with an audit trail.",
      },
      {
        question: "How does it answer policy questions accurately?",
        answer:
          "We ground it in your approved internal documentation so it answers from your actual policies, and it points employees to a person when something is ambiguous.",
      },
      {
        question: "Can it page on-call for real incidents?",
        answer:
          "Yes. It triages, classifies severity by your rules, and pages the right on-call with the details gathered, rather than letting an incident sit.",
      },
    ],
    cta: "Book an internal operations voice agent discovery call",
  },
  {
    slug: "conversational-ai-agents/professional-services",
    industry: "Professional & Specialized Services",
    title: "AI Voice Agents for Legal, Education, Research & Staffing | Tier-1.io",
    metaDescription:
      "Conversational AI voice agents for law firms, education, staffing, and research — client intake, tutoring, candidate screening, and surveys built on ElevenLabs and connected to your tools.",
    heroKicker: "Conversational AI for professional services",
    heroTitle: "AI voice agents for legal, education, staffing, and research",
    heroDescription:
      "Intake, screening, tutoring, and interviews are conversation-heavy and structured. We build voice agents on ElevenLabs that run them consistently and capture clean, structured results.",
    intro:
      "Professional and specialized services live on structured conversations — screening a potential client, tutoring a student, interviewing a candidate, or running a survey. Conversational AI conducts these consistently at any hour, follows your script and neutrality rules, and returns structured, scored, or summarized results your team can act on.",
    painPoints: [
      "Intake and screening calls eat senior staff time",
      "Coverage limited to business hours",
      "Inconsistent questioning produces messy data",
      "Notes and outcomes are captured unevenly",
    ],
    agents: [
      {
        name: "Legal Intake Specialist",
        description:
          "Screens potential clients, collects case details, and schedules attorney consultations.",
        integrations: 1,
      },
      {
        name: "General Staffing / Temp Agency Agent",
        description:
          "Matches job seekers to open positions — collects skills, availability, and preferences.",
        integrations: 2,
      },
      {
        name: "Education & Tutoring",
        description:
          "Supports educational and teaching scenarios for students.",
        integrations: 1,
      },
      {
        name: "Language Practice Tutor",
        description:
          "Interactive language learning that adapts to level, corrects, and teaches vocabulary.",
        integrations: 0,
      },
      {
        name: "Corporate Training Roleplay Coach",
        description:
          "Simulates business scenarios for training with performance scoring.",
        integrations: 0,
      },
      {
        name: "Assessment / Quiz Agent",
        description:
          "Conducts voice assessments, scores results, and provides detailed feedback.",
        integrations: 0,
      },
      {
        name: "User Research Interviewer",
        description:
          "Conducts qualitative interviews with open-ended, neutral probing that never leads.",
        integrations: 0,
      },
      {
        name: "Survey & Feedback Intake",
        description:
          "Outbound agent that conducts research or collects feedback.",
        integrations: 1,
      },
      {
        name: "Outbound Customer Feedback Collector",
        description:
          "Collects post-interaction ratings, NPS, and open-ended feedback.",
        integrations: 1,
      },
      {
        name: "Personal Voice Assistant",
        description:
          "General-purpose assistant that answers questions, takes notes, and routes.",
        integrations: 1,
      },
    ],
    outcomes: [
      "Consistent intake, screening, and interviewing at any hour",
      "Senior staff time reserved for substantive work",
      "Clean, structured, scored results instead of loose notes",
      "Higher response rates on surveys and feedback",
    ],
    integrationsNote:
      "Connects to your CRM, scheduling, and data tools so intake, screening, and feedback flow into the systems your team already uses.",
    faqs: [
      {
        question: "Can a legal intake agent screen for conflicts?",
        answer:
          "It collects the case details and information you specify and flags or routes based on your rules. It does not give legal advice; consultations are scheduled with your attorneys.",
      },
      {
        question: "Will research interviews stay neutral?",
        answer:
          "Yes. We design the agent to ask open-ended, non-leading questions and probe neutrally, so the qualitative data you collect is not biased by the questioning.",
      },
      {
        question: "How are results delivered?",
        answer:
          "Depending on the workflow, you get structured records, scores, transcripts, or summaries written into your tools — not just a pile of recordings.",
      },
    ],
    cta: "Book a professional services voice agent discovery call",
  },
];

export default agentIndustries;
